import { Avatar, Group, Text } from '@mantine/core'
import React, { forwardRef, memo, useRef, useState } from 'react'

import { setaDropdown } from '../../../utils/alternanciaIcones'
import Tooltip from '../Tooltip'

import * as S from './styles'
import { GlobalStyles } from './styles'

/**
 * @typedef {Object} Option
 * @property {string} label
 * @property {string} value
 * @property {string} [image]
 * @property {string} [description]
 */

/**
 * @typedef {Object} SelectProps
 * @property {string | null | undefined} [value]
 * @property {(value: string | null) => void} [onChange]
 * @property {string} [placeholder]
 * @property {React.ReactNode} [label]
 * @property {string | number} [width]
 * @property {Option[]} [options]
 * @property {boolean} [disabled]
 * @property {boolean} [withAsterisk]
 * @property {number} [fontSize]
 * @property {boolean} [searchable]
 */

/**
 * @param {SelectProps} props
 */
export default function Select({
  value,
  onChange,
  placeholder,
  label,
  width,
  options,
  disabled = false,
  withAsterisk = false,
  fontSize = 20,
  searchable = false,
  error
}) {
  const [searchValue, setSearchChange] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const refSelect = useRef()

  const widthText = refSelect?.current?.getBoundingClientRect()?.width

  const CustomSelectItem = forwardRef((props, ref) => (
    <SelectItem ref={ref} width={widthText} {...props} />
  ))

  CustomSelectItem.displayName = 'CustomSelectItem'

  return (
    <>
      <GlobalStyles width={width} />
      <S.Select
        ref={refSelect}
        label={label}
        fontSize={`${fontSize}px`}
        width={width && width}
        error={error}
        placeholder={placeholder}
        itemComponent={CustomSelectItem}
        withAsterisk={withAsterisk}
        searchable={searchable}
        clearable
        withinPortal
        zIndex={9999}
        radius="xl"
        value={value}
        disabled={disabled}
        onSearchChange={setSearchChange}
        searchValue={searchValue}
        nothingFound="Nenhuma opção encontrada"
        data={options || []}
        allowDeselect
        onChange={onChange}
        onDropdownOpen={() => setIsOpen(true)}
        onDropdownClose={() => setIsOpen(false)}
        rightSection={
          <S.Img isOpen={isOpen} alt="seta-dropdown" src={setaDropdown()} />
        }
      />
    </>
  )
}

/**
 * @param {Object} props
 * @param {string} [props.image]
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {number} [props.width]
 * @param {React.Ref<HTMLDivElement>} ref
 */
const SelectItem = memo(
  forwardRef(({ image, label, description, width, ...others }, ref) => {
    const textRef = useRef(null)

    const showTooltip = textRef?.current?.scrollWidth > width

    return (
      <Tooltip
        disabled={!showTooltip}
        width={width}
        withArrow
        label={label}
        position="bottom"
      >
        <div ref={ref} {...others}>
          <Group noWrap>
            {image && <Avatar src={image} />}
            <div style={{ width }}>
              <S.TextItem addOverflow={showTooltip} ref={textRef}>
                {label}
              </S.TextItem>
              {description && (
                <Text size="xs" opacity={0.65}>
                  {description}
                </Text>
              )}
            </div>
          </Group>
        </div>
      </Tooltip>
    )
  })
)
SelectItem.displayName = 'SelectItem'
